const validStringRegExp = /^[a-z\s&'-]*$/i;
// RegExp constructor avoids the TS "target" restriction on unicode-flag regex literals
const validDisplayNameCharacterRegExp = new RegExp("^[\\p{L}0-9 '\\u2018\\u2019.&-]$", 'u');
const letterRegExp = new RegExp('^\\p{L}$', 'u');
const combiningMarkRegExp = new RegExp('^\\p{M}$', 'u');
const validZipCodeRegExp = /(^\d{5}$)/;
const validPostCodeRegExp =
  /(^(([A-Z][0-9]{1,2})|(([A-Z][A-HJ-Y][0-9]{1,2})|(([A-Z][0-9][A-Z])|([A-Z][A-HJ-Y][0-9]?[A-Z])))) [0-9][A-Z]{2}$)/; // UK

export const validStringRule = (errorMessage: string) => (data: string) =>
  !!data.match(validStringRegExp) || errorMessage;

/** Validate equivalent accents identically without altering the customer's saved value. */
export const validDisplayNameRule = (errorMessage: string) => (data?: string) => {
  let acceptsCombiningMark = false;
  const isValid = Array.from((data || '').normalize('NFC')).every((character) => {
    if (combiningMarkRegExp.test(character)) return acceptsCombiningMark;
    acceptsCombiningMark = letterRegExp.test(character);
    return validDisplayNameCharacterRegExp.test(character);
  });
  return isValid || errorMessage;
};

/** Count NFC-normalized Unicode code points, including spaces, rather than UTF-16 code units. */
export const validDisplayNameLengthRule = (errorMessage: string) => (data?: string) =>
  Array.from((data || '').normalize('NFC')).length <= 80 || errorMessage;

export const validZipCodeRule = (errorMessage: string) => (zip: string) => {
  const theme = process.env.REACT_APP_THEME;
  switch (theme) {
    case 'uk':
      return !!zip.match(validPostCodeRegExp) || errorMessage;
    default:
      return !!zip.match(validZipCodeRegExp) || errorMessage;
  }
};

export const getThemeZipLength = () => {
  const theme = process.env.REACT_APP_THEME;
  switch (theme) {
    case 'uk':
      return { min: 5, max: 8 };
    default:
      return { min: 5, max: 5 };
  }
};

// temporary regexs and error messages to match email form fields to know her validation
const validStringWithDotRegExp = /^[a-z\s'\-.]*$/i; // valid string with space, ', ., or -
const validEmailRegExp =
  // eslint-disable-next-line security/detect-unsafe-regex -- Repeated segments exclude their dot separator, preventing overlapping matches.
  /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/i;
const repeatingCharacterExp = /(\w|\w\w)\1{3}/i; // repeating character 4 times, case insensitive
const restrictedNames = ['null', 'test', 'tester', 'testing', 'testin', 'delete'];
const beginsWithAcceptedSymbol = /^\B[\s'-.]/; // begins with space, ', ., or -

// temporary function to match email form fields to know her validation
export const validateNames = (value: string, translate: (string) => string): string | boolean => {
  if (!validStringWithDotRegExp.test(value)) return translate('addressUtil.restrictedCharactersError');
  if (beginsWithAcceptedSymbol.test(value)) return translate('addressUtil.alphabeticalCharacterError');
  if (restrictedNames.includes(value.toLowerCase())) return translate('addressUtil.validNameError');
  if (repeatingCharacterExp.test(value)) return translate('addressUtil.repeatingCharacterError');
  return true;
};

export const validateEmail = (value: string, errorMessage: string): string | boolean =>
  validEmailRegExp.exec(value)?.[0] === value || errorMessage;
